import { useCallback, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  Stack,
  Text,
  Textarea,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Plus } from 'lucide-react';
import { ContentGenerationLayout } from '@duke-hq/ui/components/ContentGeneration/ContentGenerationLayout';
import { ContentListItem } from '@duke-hq/ui/components/ContentGeneration/ContentListItem';
import {
  editAction,
  deleteAction,
} from '@duke-hq/ui/components/ContentGeneration/contentListItemActions';
import { ListCard } from '@duke-hq/ui/components/Cards/ListCard';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { withPermission } from '../withPermission';
import { trpcAdmin, type RouterOutputs } from '../lib/trpcAdmin';
import { testIds } from './testIds';

type ContentRole = RouterOutputs['contentRoles']['all'][number];

export const ContentRolesPage = withPermission('canManageCourseContent', () => {
  const toast = useToast();
  const drawer = useDisclosure();
  const deleteDialog = useDisclosure();

  const [selectedRole, setSelectedRole] = useState<ContentRole | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<ContentRole | null>(null);

  const rolesQuery = trpcAdmin.contentRoles.all.useQuery();

  const createMutation = trpcAdmin.contentRoles.create.useMutation({
    onSuccess: () => {
      toast({ title: t`Role created`, status: 'success', duration: 3000 });
      void rolesQuery.refetch();
      handleCloseDrawer();
    },
    onError: (error) => {
      toast({
        title: t`Failed to create role`,
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
  });

  const updateMutation = trpcAdmin.contentRoles.update.useMutation({
    onSuccess: () => {
      toast({ title: t`Role updated`, status: 'success', duration: 3000 });
      void rolesQuery.refetch();
      handleCloseDrawer();
    },
    onError: (error) => {
      toast({
        title: t`Failed to update role`,
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
  });

  const archiveMutation = trpcAdmin.contentRoles.archive.useMutation({
    onSuccess: () => {
      toast({ title: t`Role deleted`, status: 'success', duration: 3000 });
      void rolesQuery.refetch();
      deleteDialog.onClose();
      setRoleToDelete(null);
    },
    onError: (error) => {
      toast({
        title: t`Failed to delete role`,
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
  });

  const handleCloseDrawer = useCallback(() => {
    drawer.onClose();
    setSelectedRole(null);
  }, [drawer]);

  const handleOpenCreate = useCallback(() => {
    setSelectedRole(null);
    drawer.onOpen();
  }, [drawer]);

  const handleOpenEdit = useCallback(
    (role: ContentRole) => {
      setSelectedRole(role);
      drawer.onOpen();
    },
    [drawer],
  );

  const handleOpenDelete = useCallback(
    (role: ContentRole) => {
      setRoleToDelete(role);
      deleteDialog.onOpen();
    },
    [deleteDialog],
  );

  const handleConfirmDelete = useCallback(() => {
    if (roleToDelete) {
      archiveMutation.mutate(roleToDelete.id);
    }
  }, [roleToDelete, archiveMutation]);

  const handleSubmit = useCallback(
    (data: { title: string; description: string }) => {
      if (selectedRole) {
        updateMutation.mutate({ id: selectedRole.id, ...data });
      } else {
        createMutation.mutate(data);
      }
    },
    [selectedRole, updateMutation, createMutation],
  );

  const roles = rolesQuery.data ?? [];
  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <ContentGenerationLayout
        breadcrumb={<Trans>Unit Generation</Trans>}
        title={<Trans>Content Roles</Trans>}
        actions={
          <Button
            leftIcon={<Plus size={16} />}
            colorScheme="brand"
            size="sm"
            onClick={handleOpenCreate}
            data-testid={testIds.addButton}
          >
            <Trans>Add new role</Trans>
          </Button>
        }
        drawer={
          drawer.isOpen
            ? {
                isOpen: true,
                onClose: handleCloseDrawer,
                title: <Trans>Role Settings</Trans>,
                body: <RoleForm role={selectedRole} onSubmit={handleSubmit} isSaving={isSaving} />,
              }
            : undefined
        }
      >
        {rolesQuery.isPending ? (
          <Spinner data-testid={testIds.loadingSpinner} />
        ) : roles.length === 0 ? (
          <Text color="fg.muted" data-testid={testIds.emptyState}>
            <Trans>No content roles yet. Add a new role to get started.</Trans>
          </Text>
        ) : (
          <ListCard
            cardTitle={<Trans>Roles</Trans>}
            cardSubtitle={
              <Trans>
                Behavioural profiles to be utilised in the Duke Content Creation Pipeline
              </Trans>
            }
            cardContent={
              <VStack spacing="2" data-testid={testIds.rolesList}>
                {roles.map((role) => (
                  <ContentListItem
                    key={role.id}
                    title={role.title}
                    description={role.description}
                    onClick={() => handleOpenEdit(role)}
                    menuActions={[
                      editAction(() => handleOpenEdit(role)),
                      deleteAction(() => handleOpenDelete(role)),
                    ]}
                  />
                ))}
              </VStack>
            }
          />
        )}
      </ContentGenerationLayout>

      <ConfirmationDialog
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.onClose}
        title={<Trans>Delete Role</Trans>}
        body={
          <Text data-testid={testIds.deleteDialog}>
            <Trans>
              Are you sure you want to delete &quot;{roleToDelete?.title}&quot;? This action cannot
              be undone.
            </Trans>
          </Text>
        }
        onConfirm={handleConfirmDelete}
        confirmLabel={<Trans>Delete</Trans>}
        variant="destructive"
        isLoading={archiveMutation.isPending}
      />
    </>
  );
});

// -- Role form --------------------------------------------------------

interface RoleFormProps {
  role: ContentRole | null;
  onSubmit: (data: { title: string; description: string }) => void;
  isSaving: boolean;
}

function RoleForm({ role, onSubmit, isSaving }: RoleFormProps) {
  const [title, setTitle] = useState(role?.title ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const isValid = title.trim().length > 0 && description.trim().length > 0;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isValid) {
      onSubmit({ title: title.trim(), description: description.trim() });
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing="5">
        <FormControl isRequired>
          <FormLabel>
            <Trans>Title</Trans>
          </FormLabel>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t`e.g. Educator`}
            maxLength={255}
            data-testid={testIds.titleInput}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>
            <Trans>Description</Trans>
          </FormLabel>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t`Describe what this role does...`}
            maxLength={1000}
            rows={4}
            data-testid={testIds.descriptionInput}
          />
        </FormControl>

        <Button
          type="submit"
          colorScheme="brand"
          isLoading={isSaving}
          isDisabled={!isValid}
          data-testid={testIds.saveButton}
        >
          {role ? <Trans>Update</Trans> : <Trans>Save</Trans>}
        </Button>
      </Stack>
    </form>
  );
}
